import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { SIX_TOPIC } from '@/config/networkConstants';
import type { P2PStackOptions } from '@/services/abstract/poc/P2PStackManager';
import { incomingMessageReceived } from '@/store/actions/poc/incomingMessageReceived';
import { getConnectionInfoThunk } from '@/store/thunks/poc/connection/getConnectionInfoThunk';

interface InitializeP2PStackResult {
  sixId: string;
  peerId: string;
  peers: number;
  messagingInitialized: boolean;
  subscribedTopics: string[];
}

export const initializeP2PStackThunk = createAppAsyncThunk<
  InitializeP2PStackResult,
  P2PStackOptions
>(
  'connection/initializeP2PStack',
  async(options, { dispatch, extra: { pocServices }, rejectWithValue }) => {
    try {
      await pocServices.p2pStackManagerService.initializeStack({
        ...options,
        onMessage: (result, stats) => {
          dispatch(incomingMessageReceived({
            sixId: options.sixId,
            result,
            stats,
          }));
        },
        onConnectionUpdate: () => {
          void dispatch(getConnectionInfoThunk({ sixId: options.sixId }));
        },
      });

      const libp2pNode = pocServices.p2pStackManagerService.getLibp2pNode(options.sixId);
      if (libp2pNode === null) {
        throw new Error('LibP2P node not ready after initialization');
      }

      return {
        sixId: options.sixId,
        peerId: libp2pNode.peerId.toString(),
        peers: libp2pNode.getPeers().length,
        messagingInitialized: options.autoInitializeMessaging !== false,
        subscribedTopics:
          options.autoInitializeMessaging === false ? [] : [SIX_TOPIC(options.sixId)],
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ message });
    }
  },
  {
    condition: (options, { getState }) => {
      const existingConnection = getState().connection.connections.find(
        (connection) => connection.sixId === options.sixId
      );

      if (existingConnection == null) {
        return true;
      }

      return (
        existingConnection.status !== 'connecting' &&
        !existingConnection.isP2PStackInitialized
      );
    },
  }
);
